import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

// Redeems Supabase email-change confirmation links (the token_hash flow).
//
// With "Secure email change" ON, GoTrue requires BOTH the old and the new
// address to confirm. The dashboard "Change Email Address" template points its
// link here:
//   {{ .SiteURL }}/auth/confirm?token_hash={{ .TokenHash }}&type=email_change
// Each leg carries its own token_hash; both verify with type 'email_change'.
//
// We read the leg state from the verifyOtp RETURN (never a follow-up getUser(),
// which can briefly return stale pre-confirmation state):
//   - user.new_email still present (pending) → first leg done, check the other inbox
//   - user.new_email cleared (change committed) → done
// The check is order-independent: it works whichever leg the user clicks first.
//
// This route is PUBLIC and may be opened logged-out or in a different browser,
// so we never silently establish a session — see the COMPLETE handling below.
export default function EmailChangeConfirm() {
  // 'checking' | 'leg1' | 'complete' | 'expired' | 'invalid'
  const [state, setState] = useState('checking')
  // When the change is complete but no session existed in this browser, the
  // user must sign in with their new address rather than being logged in here.
  const [needsSignin, setNeedsSignin] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function confirm() {
      const params = new URLSearchParams(window.location.search)
      const tokenHash = params.get('token_hash')
      const type = params.get('type')

      if (!tokenHash || type !== 'email_change') {
        if (!cancelled) setState('invalid')
        return
      }

      // Did this browser already have a session before we verified? Determines
      // whether COMPLETE refreshes the existing session or tells the user to
      // sign in. Captured BEFORE verifyOtp, which may itself mint a session.
      const { data: pre } = await supabase.auth.getSession()
      const hadSession = !!pre?.session

      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'email_change',
      })

      if (cancelled) return

      if (error) {
        // Expired, already-used, or otherwise invalid token. Keep the message
        // vague — don't leak which.
        setState('expired')
        return
      }

      const user = data?.user
      const stillPending = !!user?.new_email && user.new_email !== user.email

      if (stillPending) {
        // First of two confirmations done; the change is not committed yet.
        if (!hadSession) {
          // Don't leave a silently-minted session behind in this browser.
          await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
        }
        if (!cancelled) setState('leg1')
        return
      }

      // Change committed (new_email cleared; user.email is now the new address).
      if (hadSession) {
        // Refresh so the displayed email updates across the app.
        await supabase.auth.refreshSession().catch(() => {})
        if (!cancelled) { setNeedsSignin(false); setState('complete') }
      } else {
        // Logged-out / different-browser confirmation: discard any session
        // verifyOtp minted locally and send the user to sign in fresh.
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {})
        if (!cancelled) { setNeedsSignin(true); setState('complete') }
      }
    }

    confirm()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="auth-page">
      <div className="auth-panel">
        <div className="auth-panel__blobs">
          <div className="auth-panel__blob auth-panel__blob--1" />
          <div className="auth-panel__blob auth-panel__blob--2" />
          <div className="auth-panel__blob auth-panel__blob--3" />
        </div>
        <div className="auth-panel__content">
          <Link to="/" className="auth-panel__logo">
            <div className="auth-panel__logo-icon">V</div>
            Verse
          </Link>
          <blockquote className="auth-panel__quote">
            "Every great story starts with a single word. Yours is waiting."
          </blockquote>
        </div>
      </div>

      <div className="auth-form-panel">
        <div className="auth-form-wrap">
          {state === 'checking' && (
            <div className="auth-form-header">
              <p className="auth-form-subtitle">Confirming your email change…</p>
            </div>
          )}

          {state === 'leg1' && (
            <div className="auth-form-header">
              <h1 className="auth-form-title">First confirmation received</h1>
              <p className="auth-form-subtitle">
                Check your other email inbox and click that link to finish the change.
              </p>
              <Link to="/settings" className="btn btn-primary auth-submit" style={{ marginTop: 24, display: 'block', textAlign: 'center' }}>
                Back to settings
              </Link>
            </div>
          )}

          {state === 'complete' && (
            <div className="auth-form-header">
              <h1 className="auth-form-title">Email address updated</h1>
              <p className="auth-form-subtitle">
                {needsSignin
                  ? 'Your email address has been updated. Sign in with your new address.'
                  : 'Your email address has been updated.'}
              </p>
              <Link
                to={needsSignin ? '/login' : '/settings'}
                className="btn btn-primary auth-submit"
                style={{ marginTop: 24, display: 'block', textAlign: 'center' }}
              >
                {needsSignin ? 'Sign in' : 'Back to settings'}
              </Link>
            </div>
          )}

          {state === 'expired' && (
            <div className="auth-form-header">
              <h1 className="auth-form-title">Invalid link</h1>
              <p className="auth-form-subtitle">
                This link is expired or has already been used.
              </p>
              <Link to="/settings" className="btn btn-primary auth-submit" style={{ marginTop: 24, display: 'block', textAlign: 'center' }}>
                Back to settings
              </Link>
            </div>
          )}

          {state === 'invalid' && (
            <div className="auth-form-header">
              <h1 className="auth-form-title">Invalid confirmation link</h1>
              <p className="auth-form-subtitle">
                This link is missing required information.
              </p>
              <Link to="/settings" className="btn btn-primary auth-submit" style={{ marginTop: 24, display: 'block', textAlign: 'center' }}>
                Back to settings
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
